import React, { useEffect, useReducer, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import postSubmitReducer, {
  initSubmitState,
} from '@/dashboard/components/SubmitButtonLoader/submitAppStateReducer';
import Button from '@/components/Button/Button';
import { useDashboardCountsContext } from '@/dashboard/provider/DashboardCountsProvider';
import { dashboardApi } from '@/dashboard/services/dashboardApi';
import tokens from '@/dashboard/tokens.json';
import { JobStatusResponse, SubmitBatchResponse } from '@/dashboard/services/dashboardApi';
import { useAlertContext } from '@/core/providers/AlertContext';
import { useDashboardContext } from '@/dashboard/provider/DashboardContextProvider';
import { hasPermission } from '@/authorization/AuthorizationGate/AuthorizationGate';
import { SCOPES } from '@/authorization/authorization.constants';
import SubmitBatchConfirmationDialog from '@/components/AppDialog/SubmitBatchConfirmationDialog';
interface SubmitButtonLoaderProps {
  numericMonth: string;
  readableMonth: string;
  visibility: boolean | null;
}

function SubmitButtonLoader({ numericMonth, readableMonth, visibility }: SubmitButtonLoaderProps) {
  const [confirmation, setConfirmation] = useState(false);
  const { counts } = useDashboardCountsContext();
  const { batchSubmitActions, setSubmitError, setSubmitProgress, setIsPolling } =
    useDashboardContext(); // Added setSubmitProgress
  const { useSubmitAsyncMutation, usePollSubmitMutation } = dashboardApi;
  const [submitBatchAsync, { isLoading }] = useSubmitAsyncMutation();
  const [startPollingSubmit] = usePollSubmitMutation();
  const [{ state: appState }, dispatch] = useReducer(
    postSubmitReducer,
    readableMonth,
    initSubmitState,
  );
  const { setIsAlertActive, setContextAlertOptions } = useAlertContext();

  function hasResultErrors(resp?: SubmitBatchResponse | JobStatusResponse | null) {
    if (!resp) return false;
    if (
      Array.isArray((resp as SubmitBatchResponse).erErrors) &&
      (resp as SubmitBatchResponse).erErrors.length
    )
      return true;
    const dwRaw = (resp as JobStatusResponse).dwResult;
    let dwAny = undefined;
    if (!dwRaw) return false;
    if (typeof dwRaw === 'string') {
      try {
        dwAny = JSON.parse(dwRaw);
      } catch (e) {
        // dwResult not JSON — nothing to inspect
        return false;
      }
    } else {
      dwAny = dwRaw;
    }
    if (!dwAny) return false;
    if (Array.isArray(dwAny)) return dwAny.length > 0;
    if (typeof dwAny === 'object') {
      if ('erErrors' in dwAny && Array.isArray(dwAny.erErrors)) return dwAny.erErrors.length > 0;
      if ('failed' in dwAny) return Boolean(dwAny.failed);
      if ('errorCount' in dwAny) return Number(dwAny.errorCount) > 0;
    }
    return false;
  }

  function reduceMessage(header: string, details?: string) {
    return header.replace('{n}', readableMonth || 'unknown') + (details ? ' ' + details : '');
  }

  const handleOnConfirm = async () => {
    setConfirmation(false);
    setSubmitError(null);
    batchSubmitActions.submitStart();
    setIsPolling(true);
    try {
      const response = await submitBatchAsync({ employerId }).unwrap();
      const INITIAL_POLL_INTERVAL_MS = 2000; // Start at 20s (or adjust)
      let pollInterval = INITIAL_POLL_INTERVAL_MS;
      const startTime = Date.now();
      const TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes
      while (true) {
        if (Date.now() - startTime > TIMEOUT_MS) {
          const timeoutMsg = 'The submission timed out after 20 minutes. Please try again.';
          setIsPolling(false);
          dispatch({ type: 'REQUEST_ERROR', error: timeoutMsg });
          try {
            setContextAlertOptions({
              mui: { severity: 'error', onClose: () => setIsAlertActive(false) },
              custom: { description: timeoutMsg, closable: true },
              app: {},
            });
            setIsAlertActive(true);
          } catch (e) {
            console.log('Failed setting global timeout alert', e);
          }
          setSubmitError(timeoutMsg);
          break;
        }
        const jobResponse = await startPollingSubmit({
          jobId: response.jobId,
          employerId: employerId,
        }).unwrap();
        setSubmitProgress(Number(jobResponse.progress) || 0);

        if (jobResponse.status === 'Succeeded') {
          // if ER or DW results indicate errors, treat as batch error
          if (hasResultErrors(jobResponse)) {
            const msg = reduceMessage(tokens['BATCH_SUBMIT_FAILURE']);
            dispatch({ type: 'BATCH_ERROR', message: msg, data: jobResponse });
            try {
              setContextAlertOptions({
                mui: { severity: 'error', onClose: () => setIsAlertActive(false) },
                custom: { description: msg, closable: true },
                app: {},
              });
              setIsAlertActive(true);
            } catch (e) {
              console.log('Failed setting global error alert', e);
            }
          } else {
            const successMessage = reduceMessage(tokens['BATCH_SUBMIT_OK']);
            dispatch({ type: 'BATCH_OK', message: successMessage, data: jobResponse });
            try {
              setContextAlertOptions({
                mui: { severity: 'success', onClose: () => setIsAlertActive(false) },
                custom: { description: successMessage, closable: true },
                app: {},
              });
              setIsAlertActive(true);
            } catch (e) {
              console.log('Failed setting global success alert', e);
            }
          }
          setIsPolling(false);
          setSubmitError(null);
          break;
        }

        if (jobResponse.status === 'Failed') {
          const msg = reduceMessage(tokens['BATCH_SUBMIT_FAILURE_HEADER'], jobResponse.message);
          setIsPolling(false);
          dispatch({ type: 'BATCH_ERROR', message: msg, data: jobResponse });
          try {
            setContextAlertOptions({
              mui: { severity: 'error', onClose: () => setIsAlertActive(false) },
              custom: { description: msg, closable: true },
              app: {},
            });
            setIsAlertActive(true);
          } catch (e) {
            console.log('Failed setting global error alert', e);
          }
          setSubmitError(msg);
          break;
        }
        await new Promise((res) => setTimeout(res, pollInterval));
        pollInterval = Math.min(pollInterval * 2, 30000);
      }
      setIsPolling(false);
      batchSubmitActions.submitEnd();
    } catch (error) {
      batchSubmitActions.submitEnd();
      setIsPolling(false);
      const catchMsg = reduceMessage(tokens['BATCH_SUBMIT_FAILURE']);
      dispatch({ type: 'REQUEST_ERROR', error: catchMsg });
      setSubmitError(catchMsg);
    }
  };

  useEffect(() => {
    if (['batch-ok', 'batch-error', 'request-error'].includes(appState)) {
      batchSubmitActions.submitEnd();
    }
    return () => setConfirmation(false);
  }, [appState]);

  // set global alert directly using AlertContext so banner appears immediately

  const employerId = counts[numericMonth]?.employerId;

  if (visibility === undefined || !employerId) {
    throw new Error(`Submit status/period missing in context/totals ${numericMonth}`);
  }

  if (visibility === true) {
    return (
      <>
        <SubmitBatchConfirmationDialog
          open={confirmation}
          setPrimaryButtonVisibility={setConfirmation}
          numericPeriod={numericMonth}
          readablePeriod={readableMonth}
          onConfirm={handleOnConfirm}
        />
        <PeriodSubmitButton
          loading={isLoading}
          disabled={isLoading}
          label={readableMonth}
          onClick={() => {
            setConfirmation(true);
          }}
        />
      </>
    );
  } else if (visibility === false) {
    return <PeriodSubmitButton disabled />;
  } else {
    return null;
  }
}

function PeriodSubmitButton({
  label,
  onClick,
  loading = false,
  disabled = false,
}: {
  disabled?: boolean;
  label?: string;
  loading?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}) {
  const ariaLabel = label
    ? loading
      ? `Submitting payroll, ${label}`
      : `Submit payroll, ${label}`
    : 'Submit';
  const canSubmit = hasPermission([SCOPES.SUBMIT_BATCH]);

  return (
    <Button
      variant="contained"
      disabled={disabled || !canSubmit}
      aria-label={ariaLabel}
      title={ariaLabel}
      onClick={canSubmit ? onClick : undefined}
      sx={{
        '&.Mui-disabled': {
          pointerEvents: 'auto',
        },
      }}
    >
      {loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 2 }} /> : null}
      Submit
    </Button>
  );
}

export default SubmitButtonLoader;
